import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Wine, RecommendChatContext, Recommendation, WineBriefContext as WineBriefCtx, WineDraft } from '@/types';

// ── Context value shape ──

export interface ScanHandoffContextValue {
  // Scan overlay
  scanOpen: boolean;
  prefillData: Partial<Wine> | null;
  openScan: (prefill?: Partial<Wine> | null) => void;
  openManualAdd: () => void;
  manualEntryDirect: boolean;
  clearManualEntryDirect: () => void;
  closeScan: () => void;
  handleWineCommitted: (docId: string | string[]) => void;
  bottleUpgradeOpen: boolean;
  closeBottleUpgrade: () => void;
  scanFABRef: React.RefObject<HTMLButtonElement | null>;

  // Wine detail
  selectedWine: Wine | null;
  setSelectedWine: (w: Wine | null) => void;

  // Recommend → Remy handoff
  recommendContext: RecommendChatContext | null;
  setRecommendContext: (ctx: RecommendChatContext | null) => void;
  handleHandoffToRemy: (ctx: RecommendChatContext) => void;
  handleAddToCellarFromRecommend: (rec: Recommendation) => void;
  handleAddToCellarFromChat: (wine: Partial<Wine>) => void;

  // Wine Brief (Scan → Remy)
  wineBriefContext: WineBriefCtx | null;
  setWineBriefContext: (ctx: WineBriefCtx | null) => void;
  handleAskRemyAboutWine: (draft: WineDraft) => void;

  // Mobile filter overlay
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (open: boolean) => void;

  // Refresh (for Pulse)
  triggerRefreshFeedback: () => void;

  // Recommend reset (nav tab re-click → back to grid)
  recommendResetKey: number;
  bumpRecommendResetKey: () => void;
}

export const ScanHandoffContext = createContext<ScanHandoffContextValue | null>(null);

export function useScanHandoff(): ScanHandoffContextValue {
  const ctx = useContext(ScanHandoffContext);
  if (!ctx) throw new Error('useScanHandoff must be used within ScanHandoffProvider');
  return ctx;
}

// ── Provider ──

interface ScanHandoffProviderProps {
  children: React.ReactNode;
  canAddBottles: (count?: number) => boolean;
  setIsSynced: (v: boolean) => void;
  /** Called by InventoryProvider after a wine is deleted, so selectedWine can be cleared */
  onWineDeletedRef: React.MutableRefObject<((wineId: string) => void) | null>;
  /** Called by InventoryProvider after a wine field is updated, so selectedWine can be synced */
  onWineUpdatedRef: React.MutableRefObject<((wineId: string, key: string, coerced: any) => void) | null>;
}

export const ScanHandoffProvider: React.FC<ScanHandoffProviderProps> = ({ children, canAddBottles, setIsSynced, onWineDeletedRef, onWineUpdatedRef }) => {
  const navigate = useNavigate();

  // ── Wine detail ──
  const [selectedWine, setSelectedWine] = useState<Wine | null>(null);

  // Register callbacks so InventoryProvider can sync selectedWine on delete/update
  onWineDeletedRef.current = useCallback((wineId: string) => {
    setSelectedWine(prev => prev?.id === wineId ? null : prev);
  }, []);

  onWineUpdatedRef.current = useCallback((wineId: string, key: string, coerced: any) => {
    setSelectedWine(prev => prev?.id === wineId ? { ...prev, [key]: coerced } : prev);
  }, []);

  // ── Scan overlay ──
  const [scanOpen, setScanOpen] = useState(false);
  const [prefillData, setPrefillData] = useState<Partial<Wine> | null>(null);
  const scanFABRef = useRef<HTMLButtonElement | null>(null);
  const [bottleUpgradeOpen, setBottleUpgradeOpen] = useState(false);

  // ── Manual entry direct (FAB long-press) ──
  const [manualEntryDirect, setManualEntryDirect] = useState(false);

  // ── Mobile filter overlay ──
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // ── Recommend handoff ──
  const [recommendContext, setRecommendContext] = useState<RecommendChatContext | null>(null);

  // ── Wine Brief (Scan → Remy) ──
  const [wineBriefContext, setWineBriefContext] = useState<WineBriefCtx | null>(null);

  // ── Recommend reset key (bumped when nav tab re-clicked) ──
  const [recommendResetKey, setRecommendResetKey] = useState(0);
  const bumpRecommendResetKey = useCallback(() => setRecommendResetKey(k => k + 1), []);

  // ── Scan callbacks ──
  const openScan = useCallback((prefill?: Partial<Wine> | null) => {
    if (!canAddBottles()) {
      setBottleUpgradeOpen(true);
      return;
    }
    setPrefillData(prefill ?? null);
    setScanOpen(true);
  }, [canAddBottles]);

  const openManualAdd = useCallback(() => {
    if (!canAddBottles()) {
      setBottleUpgradeOpen(true);
      return;
    }
    setPrefillData(null);
    setManualEntryDirect(true);
    setScanOpen(true);
  }, [canAddBottles]);

  const clearManualEntryDirect = useCallback(() => {
    setManualEntryDirect(false);
  }, []);

  const closeScan = useCallback(() => {
    setScanOpen(false);
    setPrefillData(null);
    requestAnimationFrame(() => scanFABRef.current?.focus());
  }, []);

  const closeBottleUpgrade = useCallback(() => {
    setBottleUpgradeOpen(false);
  }, []);

  const handleWineCommitted = useCallback((docId: string | string[]) => {
    setScanOpen(false);
    navigate({ to: '/cellar' });
    const ids = Array.isArray(docId) ? docId : [docId];
    requestAnimationFrame(() => {
      setTimeout(() => {
        ids.forEach((id, i) => {
          const el = document.getElementById(`wine-card-${id}`);
          if (el) {
            if (i === 0) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('rc-highlight-pulse');
            setTimeout(() => el.classList.remove('rc-highlight-pulse'), 2000);
          }
        });
      }, 300);
    });
  }, [navigate]);

  // ── Recommend / Chat handoff ──
  const handleHandoffToRemy = useCallback((ctx: RecommendChatContext) => {
    setRecommendContext(ctx);
    const isPinned = window.matchMedia('(min-width: 1440px)').matches;
    if (!isPinned) {
      navigate({ to: '/remy' });
    }
  }, [navigate]);

  const handleAddToCellarFromRecommend = useCallback((rec: Recommendation) => {
    setPrefillData({
      producer: rec.producer,
      name: rec.name,
      vintage: rec.vintage,
      type: rec.type,
    });
    setScanOpen(true);
  }, []);

  const handleAddToCellarFromChat = useCallback((wine: Partial<Wine>) => {
    setPrefillData(wine);
    setScanOpen(true);
  }, []);

  // ── Wine Brief handoff ──
  const handleAskRemyAboutWine = useCallback((draft: WineDraft) => {
    setWineBriefContext({
      briefId: crypto.randomUUID(),
      fields: draft.fields,
      source: draft.source,
    });
    setScanOpen(false);
    const isPinned = window.matchMedia('(min-width: 1440px)').matches;
    if (!isPinned) {
      navigate({ to: '/remy' });
    }
  }, [navigate]);

  // ── Pulse refresh feedback ──
  const triggerRefreshFeedback = useCallback(() => {
    setIsSynced(false);
    setTimeout(() => setIsSynced(true), 500);
  }, [setIsSynced]);

  const value = useMemo<ScanHandoffContextValue>(() => ({
    scanOpen,
    prefillData,
    openScan,
    openManualAdd,
    manualEntryDirect,
    clearManualEntryDirect,
    closeScan,
    handleWineCommitted,
    bottleUpgradeOpen,
    closeBottleUpgrade,
    scanFABRef,
    selectedWine,
    setSelectedWine,
    recommendContext,
    setRecommendContext,
    handleHandoffToRemy,
    handleAddToCellarFromRecommend,
    handleAddToCellarFromChat,
    wineBriefContext,
    setWineBriefContext,
    handleAskRemyAboutWine,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    triggerRefreshFeedback,
    recommendResetKey,
    bumpRecommendResetKey,
  }), [
    scanOpen, prefillData, openScan, openManualAdd, manualEntryDirect,
    clearManualEntryDirect, closeScan, handleWineCommitted, bottleUpgradeOpen,
    closeBottleUpgrade, selectedWine, recommendContext, handleHandoffToRemy,
    handleAddToCellarFromRecommend, handleAddToCellarFromChat, wineBriefContext,
    handleAskRemyAboutWine, mobileFiltersOpen, triggerRefreshFeedback,
    recommendResetKey, bumpRecommendResetKey,
  ]);

  return (
    <ScanHandoffContext.Provider value={value}>
      {children}
    </ScanHandoffContext.Provider>
  );
};
